import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { ClipboardCheck } from 'lucide-react'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import { Badge } from 'components/ui/badge'

const RECENT_LIMIT = 10

function formatActivityDate(dateStr?: string | null): string {
	if (!dateStr) return ''
	const d = new Date(dateStr)
	const now = new Date()
	const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
	const sevenDaysAgo = new Date(todayStart)
	sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)

	if (d >= todayStart) {
		return d.toLocaleTimeString('en-US', {
			hour: 'numeric',
			minute: '2-digit',
			second: '2-digit',
			hour12: true,
		})
	}
	if (d >= sevenDaysAgo) {
		return d.toLocaleDateString('en-US', {
			weekday: 'short',
			month: 'short',
			day: 'numeric',
			year: 'numeric',
		})
	}
	return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/**
 * Recent Activity sidebar for the Evaluations index — mirrors the visual
 * pattern of SidebarLocationsRecent / SidebarProjectsRecent / SidebarPortfoliosRecent.
 *
 * Derives the recents list from `state.evaluations.entities` sorted by
 * `updatedAt` desc (the evaluations slice has no dedicated `recents` array).
 */
function SidebarEvaluationsRecent() {
	const { recents, isLoading } = useSelector((state: any) => {
		const entities = (state.evaluations?.entities ?? {}) as Record<string, any>
		const all = Object.values(entities).filter(Boolean) as any[]
		const sorted = [...all].sort((a, b) => {
			const ta = new Date(a?.updatedAt || a?.createdAt || 0).getTime()
			const tb = new Date(b?.updatedAt || b?.createdAt || 0).getTime()
			return tb - ta
		})
		return {
			recents: sorted.slice(0, RECENT_LIMIT),
			isLoading: !!state.evaluations?.isLoading,
		}
	})

	const isEmpty = recents.length === 0

	return (
		<div className="flex flex-col h-full">
			<div className="px-4 py-3 border-b border-gray-100">
				<div className="flex items-center gap-2">
					<ClipboardCheck className="w-4 h-4 text-gray-400" />
					<span className="text-sm font-semibold text-gray-900">Recent Activity</span>
					{recents.length > 0 && (
						<span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
							{recents.length}
						</span>
					)}
				</div>
			</div>
			<div className="flex-1 overflow-y-auto">
				{isLoading ? (
					<div className="flex items-center justify-center h-full p-4">
						<span className="text-sm text-gray-400">Loading...</span>
					</div>
				) : isEmpty ? (
					<div className="text-center py-6">
						<ClipboardCheck className="w-5 h-5 mx-auto text-gray-300 mb-2" />
						<p className="text-sm text-gray-400">No recent evaluations</p>
					</div>
				) : (
					<RecentList recents={recents} />
				)}
			</div>
		</div>
	)
}

function RecentList({ recents }: { recents: any[] }) {
	return (
		<div className="flow-root px-4 pt-3">
			<ul className="-mb-6">
				{recents.map((evaluation, idx) => (
					<EvaluationRow
						key={evaluation._id || idx}
						evaluation={evaluation}
						isLast={idx === recents.length - 1}
					/>
				))}
			</ul>
		</div>
	)
}

function EvaluationRow({ evaluation, isLast }: { evaluation: any; isLast: boolean }) {
	// First populated site avatar (if any) — same source the Home dashboard
	// Evaluations card uses for its small avatar.
	const firstSiteImage = useMemo<string | undefined>(() => {
		const avatars: any[] = evaluation?.siteAvatars ?? []
		const withImage = avatars.find(a => a?.image)
		return withImage?.image
	}, [evaluation?.siteAvatars])

	const name = evaluation?.name || 'Untitled evaluation'
	const initial = name.charAt(0).toUpperCase()
	const status = evaluation?.status || 'DRAFT'

	return (
		<li className="relative pb-6 last:pb-0">
			{!isLast && (
				<span
					className="absolute left-[15px] top-8 -ml-px h-full w-0.5 bg-gray-200"
					aria-hidden="true"
				/>
			)}
			<Link
				to={`/evaluations/${evaluation._id}`}
				className="relative flex items-start gap-3 no-underline group"
			>
				<Avatar className="h-8 w-8 flex-shrink-0">
					{firstSiteImage ? (
						<>
							<AvatarImage src={firstSiteImage} alt={name} />
							<AvatarFallback className="text-xs bg-gray-200 text-gray-600">
								{initial}
							</AvatarFallback>
						</>
					) : (
						<AvatarFallback className="bg-blue-50 text-blue-600">
							<ClipboardCheck className="w-3.5 h-3.5" />
						</AvatarFallback>
					)}
				</Avatar>
				<div className="min-w-0 flex-1 pt-0.5">
					<span className="block text-sm font-medium text-gray-900 truncate group-hover:text-[#0080fe] transition-colors">
						{name}
					</span>
					<div className="mt-1 flex items-center gap-1.5">
						<Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
							{status.replace(/_/g, ' ')}
						</Badge>
						{evaluation?.marketArea && (
							<span className="text-xs text-gray-400 truncate">{evaluation.marketArea}</span>
						)}
					</div>
					{evaluation?.updatedAt && (
						<p className="mt-0.5 text-xs text-gray-400">
							{formatActivityDate(evaluation.updatedAt)}
						</p>
					)}
				</div>
			</Link>
		</li>
	)
}

export default SidebarEvaluationsRecent
